import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Paper, Card, CardContent, Typography, Divider, CircularProgress, Box, Alert, Stack, Chip, IconButton, Collapse } from '@mui/material';
import { Star, Feedback, EventNote, Person, Close, ExpandMore } from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';

const FeedbackDetails = () => {
  const { id } = useParams();
  const [feedbackData, setFeedbackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        const response = await fetch(`http://42.0.0.1:8001/get-feedback/?enquiry_id=${id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch feedback details');
        }
        const data = await response.json();
        setFeedbackData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();
  }, [id]);

  if (loading) return <Box textAlign="center" my={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const toggleExpand = (field) => {
    setExpanded((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (



    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" py={4} sx={{ bgcolor: '#f0f4f8', px: 2 }}>
      <Card sx={{
        maxWidth: 800,
        width: '100%',
        boxShadow: 4,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'transform 0.3s',
        bgcolor: 'white',
        border: '1px solid #e0e0e0',
      }}>
        {/* Header Section */}
        <Box bgcolor="#3186C9" color="white" p={2} textAlign="center" sx={{ borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" fontWeight="bold" sx={{ letterSpacing: 1, marginTop: '20px' }}>
              Feedback Details
            </Typography>
            <IconButton color="inherit" onClick={() => window.history.back()} sx={{ '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' } }}>
              <Close />
            </IconButton>
          </Stack>
          <Typography variant="subtitle1" sx={{ fontStyle: 'italic', mt: 1, fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.85)' }}>
            {feedbackData.demo_product}
          </Typography>
          <Box mt={1}>
            <Typography variant="body2" sx={{ fontWeight: 'light', color: 'rgba(255, 255, 255, 0.7)' }}>
              Your insights matter to us! Please find the detailed feedback below.
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {/* Ratings Section */}
          <Box mb={4} sx={{ borderLeft: 3, borderColor: '#3186C9', pl: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#DC143C', mb: 2 }}>
              <Feedback sx={{ color: '#DC143C' }} />
              <Typography sx={{ color: '#DC143C' }} variant="h6" fontWeight="bold">Ratings</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {[
              { label: 'Presentation Rating', value: feedbackData.presentation_rating },
              { label: 'Product Interest Rating', value: feedbackData.product_interest_rating },
              { label: 'Feature Relevance Rating', value: feedbackData.feature_relevance_rating }
            ].map((rating, index) => (
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} key={index}>
                <Typography sx={{ color: '#3186C9', fontWeight: 'bold', flexGrow: 1 }}>{rating.label}</Typography> {/* Make label bold */}
                <Box display="flex" alignItems="center">
                  {[...Array(5)].map((_, starIndex) => (
                    <StarIcon
                      key={starIndex}
                      sx={{ color: starIndex < rating.value ? '#3186C9' : 'action', fontSize: '1.2rem' }}
                    />
                  ))}
                  <Chip
                    label={rating.value}
                    color="secondary"
                    variant="outlined"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      bgcolor: '#3186C9',
                      color: 'white',
                      marginLeft: 1 // Space between stars and numeric value
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>

          {/* Feedback Section */}
          <Box mb={4} sx={{ borderLeft: 3, borderColor: '#3186C9', pl: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <EventNote sx={{ color: '#DC143C' }} /> {/* Set color for the icon */}
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#DC143C' }}>Feedback</Typography> {/* Set font color */}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {[
              { label: 'Positive Feedback', value: feedbackData.positive_feedback },
              { label: 'Improvement Suggestions', value: feedbackData.improvement_suggestions },
              { label: 'Additional Features Requested', value: feedbackData.additional_features_requested },
            ].map((feedback, index) => {
              const hasValue = feedback.value && feedback.value.trim() !== '';

              return (
                <Box key={index} mb={2} sx={{ transition: 'all 0.3s ease' }}>
                  {hasValue ? (
                    <>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body1"
                          onClick={() => toggleExpand(feedback.label)}
                          sx={{ cursor: 'pointer', color: '#3186C9', fontWeight: 'bold', flexGrow: 1 }}
                        >
                          {feedback.label}
                        </Typography>
                        <ExpandMore
                          sx={{
                            transform: expanded[feedback.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </Box>
                      <Collapse in={expanded[feedback.label]}>
                        <Paper
                          elevation={3}
                          sx={{
                            mt: 1,
                            padding: 2,
                            borderRadius: 2,
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-line',
                              color: '#333',
                              lineHeight: 1.5
                            }}
                          >
                            {feedback.value}
                          </Typography>
                        </Paper>
                      </Collapse>
                      <Divider sx={{ my: 1 }} />
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      <strong>{feedback.label}:</strong> No Comments
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>


          {/* Meeting Outcome Section */}
          <Box mb={4} sx={{ borderLeft: 3, borderColor: '#3186C9', pl: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ color: '#DC143C', mb: 2 }}>
              <EventNote sx={{ color: '#DC143C' }} />
              <Typography variant="h6" fontWeight="bold" sx={{ color: '#DC143C' }}>Meeting Outcome</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {[
              { label: 'Outcome', value: feedbackData.meeting_outcome },
              { label: 'Follow Up Action', value: feedbackData.follow_up_action },
              { label: 'Overall Comments', value: feedbackData.overall_comments },
            ].map((outcome, index) => {
              const hasValue = outcome.value && outcome.value.trim() !== '';

              return (
                <Box key={index} mb={2} sx={{ transition: 'all 0.3s ease' }}>
                  {hasValue ? (
                    <>
                      <Box display="flex" alignItems="center">
                        <Typography
                          variant="body1"
                          onClick={() => toggleExpand(outcome.label)}
                          sx={{ cursor: 'pointer', color: '#3186C9', fontWeight: 'bold', flexGrow: 1 }}
                        >
                          {outcome.label}
                        </Typography>
                        <ExpandMore
                          sx={{
                            transform: expanded[outcome.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </Box>
                      <Collapse in={expanded[outcome.label]}>
                        <Paper
                          elevation={3}
                          sx={{
                            mt: 1,
                            padding: 2,
                            borderRadius: 2,
                            bgcolor: '#FFFFFF',
                            border: '1px solid #E0E0E0',
                            boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-line',
                              color: '#333',
                              lineHeight: 1.5
                            }}
                          >
                            {outcome.value}
                          </Typography>
                        </Paper>
                      </Collapse>
                      <Divider sx={{ my: 1 }} />
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      <strong>{outcome.label}:</strong> No Comments
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* Footer Section */}
          <Box
            mt={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              padding: 2,
              borderRadius: 2,
              backgroundColor: '#f5f5f5', // Light background for contrast
              border: '1px solid #ddd', // Subtle border
              boxShadow: 1, // Optional: adds a slight shadow for depth
            }}
          >
            <Typography variant="body2" sx={{ color: '#333333', fontStyle: 'italic' }}>
              <Person sx={{ color: '#3186C9', marginRight: 0.5 }} /> {/* Change icon color */}
              Submitted By: {feedbackData.submitted_by}
            </Typography>
            <Typography variant="body2" sx={{ color: '#333333', fontStyle: 'italic' }}>
              <EventNote sx={{ color: '#3186C9', marginRight: 0.5 }} /> {/* Change icon color */}
              {new Date(feedbackData.submitted_on).toLocaleString()}
            </Typography>
          </Box>




        </CardContent>
      </Card>
    </Box>
  );
};

export default FeedbackDetails;
